import React, { useEffect, useMemo, useState } from 'react';
import ScoreContainer from '../ScoreContainer/ScoreContainer';
import { useDebounce } from 'react-use';
import BackendService from '../../services/BackendService';
import { useTranslation } from 'react-i18next';
import { CommandPrefixes } from '../../commands/practitioner/enums/CommandPrefixes';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import { ShowMessageCommandTypes } from '../../commands/practitioner/enums/ShowMessageCommandTypes';
import AccordionComponent from '../UI/AccordionExpandIcon/AccordionComponent';
import { ControlPanelSessionTabStyles } from './styles';
import { Box } from '@mui/system';

interface ContainerProps {
  treatmentUUID: string;
  sessionUUID: string;
}

const ControlPanelSessionTab: React.FC<ContainerProps> = ({ treatmentUUID, sessionUUID }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialised, setIsInitialised] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState('');
  const { notes, sudScores, vocScores, setNotes, setSudScores, setVocScores } = usePractitionerControlsContext();
  const { t } = useTranslation();
  const { controlPanelSessionTab } = ControlPanelSessionTabStyles;
  const SUDColors = [
    '#90FF75',
    '#9FFF75',
    '#C2FF75',
    '#E1FF75',
    '#FFF375',
    '#FFDE75',
    '#FCBF7A',
    '#F1B287',
    '#E9AA93',
    '#E2A49E',
    '#E0A1A0',
  ];
  const VOCColors = ['#9EDCE2', '#E575FF', '#CD75FF', '#9575FF', '#7583FF', '#75ACFF', '#94D3E9'];

  useDebounce(
    () => {
      sendSaveNotesCommand()
        .then(() => {
          if (isInitialised) {
            setIsSaving(true);
            setDebouncedValue(notes);
          } else {
            setIsInitialised(true);
          }
        })
        .catch(() => {
          setIsInitialised(true);
        });
    },
    500,
    [notes]
  );

  const parseNotes = (result: string[]) => {
    if (!result) {
      return;
    }
    const allNotes = result.filter((note: string) => note.includes(CommandPrefixes.NOTES));
    let lastNote = allNotes[allNotes.length - 1] || '';
    if (lastNote) {
      lastNote = lastNote.substring(5, lastNote.length - 2);
    }
    const replacedNote = lastNote.replace(CommandPrefixes.NOTES, '').replace('"', '');
    if (notes !== replacedNote) {
      setNotes(replacedNote);
    }
  };

  const parseScores = (result: string[], prefix: string) => {
    if (!result) {
      return [];
    }
    const allScores = result.filter((score: string) => score.includes(prefix));

    return allScores.map((score: string) => {
      const replacedScore = score.replace(prefix, '');
      return {
        value: parseInt(replacedScore),
        color:
          prefix === CommandPrefixes.SUD
            ? SUDColors[parseInt(replacedScore) % SUDColors.length]
            : VOCColors[parseInt(replacedScore) % VOCColors.length],
        key: Math.random()
          .toString(36)
          .slice(2),
      };
    });
  };

  useEffect(() => {
    BackendService.fetchCurrentCommandsForPractitioner(sessionUUID, treatmentUUID)
      .then((result: string[]) => {
        parseNotes(result);
        setSudScores(parseScores(result, CommandPrefixes.SUD));
        setVocScores(parseScores(result, CommandPrefixes.VOC));
      })
      .catch(e => {
        console.error(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendSaveNotesCommand = async () => {
    if (!notes) {
      return Promise.reject();
    }
    return BackendService.sendSaveNotesCommand(sessionUUID, treatmentUUID, notes);
  };

  const sendVocShowAidCommand = () => {
    BackendService.sendShowMessageCommand(sessionUUID, treatmentUUID, ShowMessageCommandTypes.Voc);
  };
  const sendSudShowAidCommand = () => {
    BackendService.sendShowMessageCommand(sessionUUID, treatmentUUID, ShowMessageCommandTypes.Sud);
  };

  const combinedAttributesToSend = useMemo(() => {
    return {
      session_completed: null, // Hardcoded toggle the correct prop
      notes: [debouncedValue],
      sud_array: sudScores.map((item: any) => item.value.toString()),
      validity_of_cognition_array: vocScores.map((item: any) => item.value.toString()),
    };
  }, [debouncedValue, vocScores, sudScores]);

  useEffect(() => {
    if (isInitialised) {
      BackendService.updateSessionData(sessionUUID, treatmentUUID, combinedAttributesToSend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedAttributesToSend, isInitialised]);

  return (
    <Box sx={controlPanelSessionTab}>
      <AccordionComponent isIcon={false} title={'measurements'} isExpanded={true}>
        <div className="general-score-container">
          <ScoreContainer
            scores={sudScores}
            setValues={setSudScores}
            sessionId={sessionUUID}
            treatmentId={treatmentUUID}
            name={t('sud')}
            type={CommandPrefixes.SUD}
            possibleScores={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            showVisualAidCommand={sendSudShowAidCommand}
          />
          <ScoreContainer
            scores={vocScores}
            setValues={setVocScores}
            sessionId={sessionUUID}
            treatmentId={treatmentUUID}
            name={t('voc')}
            type={CommandPrefixes.VOC}
            possibleScores={[1, 2, 3, 4, 5, 6, 7]}
            showVisualAidCommand={sendVocShowAidCommand}
          />
        </div>
      </AccordionComponent>
      <AccordionComponent title={'notes'} isIcon={false} isExpanded={true}>
        <div className="notes-container">
          {isSaving}
          <textarea
            className="textarea"
            rows={10}
            value={notes}
            onChange={event => {
              setIsSaving(false);
              setNotes(event.target.value);
            }}
          />
          <p className="save-notification">{isSaving ? t('successfully-saved') : ''}</p>
        </div>
      </AccordionComponent>
    </Box>
  );
};

export default ControlPanelSessionTab;
